import { LoadingSpinner } from "@tokenterminal/core/ui/progress/LoadingSpinner"
import { SearchableList } from "@tokenterminal/core/ui/samsCombobox/searchableList/SearchableList"
import { useMemo, useState } from "react"
import { initialCommands } from "./commands"
import { makeSearchResultItem } from "./makeSearchResultItem"
import { type Command } from "./types"
import { useFuzzySearchResults } from "./useFuzzySearchResults"
import {
  isAskCommand,
  useRecentSearchCommands,
} from "./useRecentSearchCommands"

export const useSearchResults = (search: string, closeSearch: () => void) => {
  const {
    addRecentSearchCommand,
    removeRecentSearchCommand,
    recentSearchCommands,
  } = useRecentSearchCommands()
  const { makeFuzzySearchResults, isLoading, allCommandsById } =
    useFuzzySearchResults()

  return useMemo(() => {
    if (isLoading || !allCommandsById) {
      return [
        <SearchableList.EmptyState key="loading" className="py-6">
          <LoadingSpinner />
        </SearchableList.EmptyState>,
      ]
    }

    // If the search is empty, show any recent searches and the initial navigation commands
    if (search.length === 0) {
      const recentSearchResultItems = Object.values(recentSearchCommands)
        .sort((a, b) => b.lastUsed - a.lastUsed)
        .map((recentSearchCommand) => {
          // "Hydrate" the recent command by id
          let command: Command | undefined = undefined
          // For the ask command, we need to grab the query from the payload
          if (isAskCommand(recentSearchCommand)) {
            command = {
              id: `ask-${encodeURIComponent(recentSearchCommand.query)}`,
              key: `ask-${encodeURIComponent(recentSearchCommand.query)}`,
              title: "Ask",
              type: "ask",
              data: { query: recentSearchCommand.query },
            }
          } else {
            command = allCommandsById.get(recentSearchCommand.id)
          }

          if (!command) {
            return null
          }

          return makeSearchResultItem({
            command: {
              ...command,
              key: `recent::${command.key}`,
            },
            handleClose: closeSearch,
            addRecentSearchCommand,
            removeRecentSearchCommand,
            isRecent: true,
          })
        })
        .filter((item) => item !== null)

      return [
        ...(recentSearchResultItems.length > 0
          ? [
              <SearchableList.Group key="recents">
                <SearchableList.GroupHeader className="!pt-0">
                  Recents
                </SearchableList.GroupHeader>
                {recentSearchResultItems.slice(0, 5)}
              </SearchableList.Group>,
            ]
          : []),
        <SearchableList.Group key="navigation">
          <SearchableList.GroupHeader
            className={recentSearchResultItems.length === 0 ? "!pt-0" : ""}
          >
            Navigation
          </SearchableList.GroupHeader>
          {initialCommands.map((item) =>
            makeSearchResultItem({
              command: item,
              handleClose: closeSearch,
              addRecentSearchCommand,
              removeRecentSearchCommand,
            })
          )}
        </SearchableList.Group>,
      ]
    }

    const fuzzySearchResults = makeFuzzySearchResults(search) || []

    // Uncomment when we introduce the ask command
    // const dynamicAskResult = makeSearchResultItem({
    //     command: {
    //       id: `ask-${encodeURIComponent(search)}`,
    //       key: `ask-${encodeURIComponent(search)}`,
    //       title: "Ask",
    //       type: "ask",
    //       data: { query: search },
    //     },
    //     handleClose: closeSearch,
    //     addRecentSearchCommand,
    //     removeRecentSearchCommand,
    //   })

    const results: React.ReactNode[] = fuzzySearchResults.map((searchResult) =>
      makeSearchResultItem({
        command: searchResult.command,
        handleClose: closeSearch,
        addRecentSearchCommand,
        removeRecentSearchCommand,
      })
    )

    // Uncomment when we introduce the ask command
    // return [...results, dynamicAskResult]

    return results.length > 0 ? (
      results
    ) : (
      <SearchableList.EmptyState className="py-6" key="empty-state">
        No results found for &quot;{search}&quot;
      </SearchableList.EmptyState>
    )
  }, [
    addRecentSearchCommand,
    allCommandsById,
    closeSearch,
    isLoading,
    makeFuzzySearchResults,
    recentSearchCommands,
    removeRecentSearchCommand,
    search,
  ])
}

/**
 * Responsible for keeping track of the search state, calling the necessary
 * hooks to generate the search results, and rendering the search UI.
 */

export const Search = ({ closeSearch }: { closeSearch: () => void }) => {
  const [search, setSearch] = useState("")
  const searchableListChildren = useSearchResults(search, closeSearch)

  return (
    <div className="w-[900px] max-w-[calc(100vw-40px)]">
      <SearchableList search={search} onChangeSearch={setSearch}>
        <SearchableList.SearchInput
          inputProps={{
            onKeyDown: (e) => {
              if (e.key === "Escape") {
                e.stopPropagation()
                if (search.length > 0) {
                  setSearch("")
                } else {
                  closeSearch()
                }
              }
            },
          }}
          autoFocus
          placeholder="Search projects, metrics, datasets, etc."
          size="large"
          className="!text-sm"
        />
        <div className="">
          <div className="w-full p-0 rounded-xl flex flex-col gap-1 text-sm max-h-[500px] overflow-y-auto !relative">
            <SearchableList.List className="p-2" shouldFilter={false}>
              {searchableListChildren}
            </SearchableList.List>
          </div>
        </div>
      </SearchableList>
    </div>
  )
}
